/* eslint-disable react-hooks/exhaustive-deps */
/**
 * AttendedFullscreenWrapper
 *
 * Shared host shell for the "attended" and "consultation/treatment-started"
 * fullscreen popups. Provides:
 *   - Fullscreen Dialog + slide-up Transition
 *   - AppBar with title, centred "Follow up N • in X days" badge, Cancel button
 *   - Right-side vertical-tab sidebar with:
 *       • sidebarPulse attention-ring animation (replays on each programmatic open)
 *       • sidebarSwitchCue slide-in animation per content panel
 *   - Equal-height left/right columns when a sidebar tab is open
 *   - Debug tab (superadmin-only, built into the tab list when isSuperuser is true)
 *
 * Props
 * -----
 * open              bool       — Dialog open state
 * onClose           func       — called when dialog should close
 * type              string     — passed to reminderDialogStyle (default 'lead')
 * headerButtons     bool       — show Cancel button in header
 * headerTitle       node       — main title content (e.g. "Attended appointment for John")
 * followLead        object     — lead data for the header badge (lead_appointment, lead_contacted_counts)
 * headerCountStatus string     — the status used to look up contactedCount in lead_contacted_counts
 *                                (e.g. 'CONSULTATION_BOOKED' or 'CONSULTATION_ATTENDED')
 * appointmentFilter func|null  — optional fn(appointments[]) → appointments[] to pick the badge datetime.
 *                                Defaults to filtering by status === 'ATTENDED'.
 * sidebarTabs       array      — tab descriptors: { icon, label, value, component, width, height }
 * sidebarTab        string     — currently active tab value (controlled by parent)
 * onTabChange       func       — (event, newValue) => void
 * sidebarPulseKey   number     — increment this to replay the attention-pulse ring
 *                                (parent bumps it whenever a tab is opened programmatically)
 * popupName        string     — name recorded with any failure pinned from this popup (e.g. "Attended")
 * pinLeadId         string|number — lead the header pin control / failure banner act on (default followLead.id)
 * pinLeadPracticeId string|number — that lead's practice (default followLead.practice, id or object); a
 *                                mismatch with the current practice hides the pin actions
 * children          node       — left panel content (the popup component)
 */
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { Box, Stack, Dialog, Button, AppBar, Typography, Slide, Tabs, Tab } from '@mui/material';
import moment from 'moment';
import Iconify from '../Iconify';
import LeadPinControl from '../../sections/@dashboard/leadDetail/LeadPinControl';
import PopupFailureBanner from './PopupFailureBanner';
import { PopupFailureProvider } from '../../contexts/PopupFailureContext';
import {
    commonDialogHeaderStyle,
    onBoardDialogHeader,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from '../../styles/ScheduleReminder';
import {
    rightBoxInnerStyle,
    leftBoxInnerStyle,
    tabsMainBoxStyle,
    tabButtonStyle,
    tabContentBoxStyle,
    popupMainStackStyle,
} from '../../styles/ContactLeadPopup';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// ---------------------------------------------------------------------------
// Header badge — "N of M tasks • X days ago / Today / In X days"
//
// Change 5: replaced "Follow up N" with checklist progress "N of M tasks".
// The small circle now shows the done count (or "N/M" when totals are known).
// Pulse toned down: scale 1.25 → 1.06, box-shadow ring softened — reads as
// "current context" rather than an urgent alert.
// ---------------------------------------------------------------------------
function FollowUpBadge({ followLead, appointmentFilter, pulseCssName, checklistDone, checklistTotal }) {
    const apptList = followLead?.lead_appointment || [];
    const filteredAppts = appointmentFilter
        ? appointmentFilter(apptList)
        : apptList.filter((el) => el.status === 'ATTENDED');
    const apptDt = filteredAppts[0]?.datetime;
    if (!apptDt) return null;

    const parsed = moment(apptDt, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', moment.ISO_8601], true);
    if (!parsed.isValid()) return null;

    const days = moment().startOf('day').diff(parsed.startOf('day'), 'days');

    let daysLabel;
    if (days === 0) {
        daysLabel = 'Today';
    } else if (days === 1) {
        daysLabel = '1 day ago';
    } else if (days < 0) {
        // Future appointment — show "In N days" (never render negative "−N days ago")
        daysLabel = `In ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
    } else {
        daysLabel = `${days} days ago`;
    }

    // Progress display: show "N of M tasks" when total is known and > 0.
    // Guard: if total is 0/unknown, omit the progress section entirely.
    const hasProgress = checklistTotal > 0;
    const circleLabel = hasProgress ? String(checklistDone) : null;
    const progressLabel = hasProgress ? `${checklistDone} of ${checklistTotal} tasks` : null;

    return (
        <Box
            sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.625,
                // Change 5: toned-down pulse — scale 1.06 (was 1.25), softer shadow ring.
                [`@keyframes ${pulseCssName}`]: {
                    '0%':   { transform: 'scale(1)',    boxShadow: '0 0 0 0 rgba(255,255,255,0.25)' },
                    '50%':  { transform: 'scale(1.06)', boxShadow: '0 0 0 3px transparent' },
                    '100%': { transform: 'scale(1)',    boxShadow: '0 0 0 0 transparent' },
                },
            }}
        >
            {/* Badge row: tasks pill + divider + days dot */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {hasProgress && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: `${pulseCssName} 2.4s ease-in-out infinite`,
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#007867',
                                lineHeight: 1,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {circleLabel}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}
                    >
                        {progressLabel}
                    </Typography>
                </Box>
            )}
            {hasProgress && (
                <Box sx={{ width: '1px', height: '16px', bgcolor: 'rgba(255,255,255,0.30)' }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.95)',
                        animation: `${pulseCssName} 2.4s ease-in-out infinite`,
                        flexShrink: 0,
                    }}
                />
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {daysLabel}
                </Typography>
            </Box>
            </Box>{/* /inner badge row */}
            {/* Wave A Change 4: thin progress bar — hidden when total unknown */}
            {hasProgress && (
                <Box
                    sx={{
                        width: '100%',
                        height: 3,
                        borderRadius: '2px',
                        bgcolor: 'rgba(255,255,255,0.25)',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            borderRadius: '2px',
                            bgcolor: 'rgba(255,255,255,0.90)',
                            width: `${checklistTotal > 0 ? (checklistDone / checklistTotal) * 100 : 0}%`,
                            transition: 'width 400ms ease',
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}

FollowUpBadge.propTypes = {
    followLead: PropTypes.object,
    appointmentFilter: PropTypes.func,
    pulseCssName: PropTypes.string.isRequired,
    /** Change 5: checklist items that are done (completed/skipped). */
    checklistDone: PropTypes.number,
    /** Change 5: total checklist items (done + active). 0 = unknown → progress hidden. */
    checklistTotal: PropTypes.number,
};

FollowUpBadge.defaultProps = {
    followLead: null,
    appointmentFilter: null,
    checklistDone: 0,
    checklistTotal: 0,
};

// ---------------------------------------------------------------------------
// Main wrapper
// ---------------------------------------------------------------------------
export default function AttendedFullscreenWrapper({
    open,
    onClose,
    type,
    headerButtons,
    headerTitle,
    followLead,
    // headerCountStatus kept in PropTypes for caller API compatibility — no longer consumed internally
    appointmentFilter,
    sidebarTabs,
    sidebarTab,
    onTabChange,
    sidebarPulseKey,
    pulseCssName,
    checklistDone,
    checklistTotal,
    popupName,
    pinLeadId,
    pinLeadPracticeId,
    children,
}) {
    // An explicit pinLeadId (even null = "no lead") always wins; followLead is only
    // the fallback when the caller says nothing, because callers that track their
    // own current lead can hold a stale global followLead.
    const resolvedLeadId = pinLeadId !== undefined ? pinLeadId : followLead?.id ?? null;
    // followLead.practice may be a bare id or a nested object.
    const resolvedPracticeId = pinLeadPracticeId !== undefined
        ? pinLeadPracticeId
        : followLead?.practice?.id ?? followLead?.practice ?? null;

    // sidebarPulseKey is owned by the parent and is incremented each time
    // a tab is opened programmatically (e.g. follow-up Email/WhatsApp redirect).
    // We use it directly as the `key` prop on the glow-ring overlay so it
    // re-mounts and replays the attention animation on every bump.
    // The glow ring only renders when sidebarPulseKey > 0 (not on initial mount).
    // Parent is responsible for resetting its counter when the dialog closes.

    const labelCount = (data) => data.label;

    // Layout-stability: determine sidebar presence from the tabs array, NOT from
    // which tab is currently selected. This decouples the layout dimensions from
    // async tab initialisation — the sidebar reserves its full width (~500/506px)
    // from the very first paint, regardless of whether sidebarTab has been set yet.
    // sidebarTab is used only for content routing (which panel to show), never for
    // sizing decisions.
    const hasSidebar = sidebarTabs.length > 0;

    return (
        <PopupFailureProvider popupName={popupName} open={open}>
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={onClose}
            sx={[
                reminderDialogStyle(type),
                {
                    pt: { xs: 0, sm: 0 },
                    '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' },
                },
            ]}
            disableEnforceFocus
        >
            {/* ── AppBar / Header ── */}
            <AppBar
                sx={[
                    headerButtons ? commonDialogHeaderStyle : onBoardDialogHeader,
                    { position: 'sticky !important', top: 0, alignItems: 'center' },
                ]}
            >
                <Typography sx={reminderTitleStyle} variant="h6" component="div">
                    {headerTitle}
                </Typography>

                <FollowUpBadge
                    followLead={followLead}
                    appointmentFilter={appointmentFilter}
                    pulseCssName={pulseCssName || 'attendedWrapperPulse'}
                    checklistDone={checklistDone}
                    checklistTotal={checklistTotal}
                />

                {headerButtons && (
                    <Box sx={[cancelBoxStyle, { alignItems: 'center' }]}>
                        {resolvedLeadId && (
                            <LeadPinControl leadId={resolvedLeadId} leadPracticeId={resolvedPracticeId} variant="header" />
                        )}
                        <Button
                            color="inherit"
                            variant="outlined"
                            style={{ borderColor: '#FFFFFF', color: '#fff' }}
                            onClick={() => onClose('close')}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}
            </AppBar>

            {/* ── Body: left content + right sidebar ── */}
            <Stack
                spacing={0}
                sx={[
                    popupMainStackStyle(hasSidebar ? 'expanded' : ''),
                    {
                        // Width driven by hasSidebar (tab array presence), NOT sidebarTab value.
                        // This keeps the left-panel width stable while the active tab resolves
                        // asynchronously — only inner content changes, never the layout dims.
                        width: hasSidebar ? 'calc(100% - 506px)' : 'calc(100% - 85px)',
                        pb: '88px',
                    },
                ]}
            >
                {/* Left panel — equal-height with the right sidebar when a tab is open */}
                <Box
                    sx={[
                        leftBoxInnerStyle(false, true),
                        { background: '#F5F5F7' },
                        hasSidebar && {
                            // dvh tracks the visible viewport height (excludes
                            // mobile browser chrome); vh is fixed to the layout
                            // viewport and can be taller than the visible area.
                            height: 'calc(100dvh - 152px)',
                            minHeight: 'calc(100dvh - 152px)',
                            '@media (max-width: 1280px)': { height: 'auto' },
                        },
                    ]}
                >
                    <PopupFailureBanner leadId={resolvedLeadId} leadPracticeId={resolvedPracticeId} />
                    {children}
                </Box>

                {/* Right panel — sidebar with vertical tabs.
                    Width driven by hasSidebar to stay stable from first paint. */}
                <Box
                    sx={[
                        rightBoxInnerStyle(false, hasSidebar ? 'expanded' : ''),
                        { top: '88px', height: 'calc(100vh - 152px)' },
                    ]}
                >
                    {/* Tab content panels — keyed by `value-sidebarTab` so each panel
                        remounts on switch and replays the entrance animation.
                        The attention glow is SCOPED to the composer tabs (email /
                        whatsapp) ONLY — it wraps just this content region, never the
                        nav-tab rail — and is keyed by sidebarPulseKey so a programmatic
                        composer-open replays it. */}
                    {sidebarTabs.map((tab) => {
                        const isMatched = tab.value === sidebarTab;
                        if (!isMatched) return false;
                        const isComposerTab = tab.value === 'email' || tab.value === 'whatsapp';
                        return (
                            <Box
                                // Re-key on sidebarPulseKey for composer tabs so a
                                // programmatic re-open (same tab) replays the entrance + glow.
                                key={`${tab.value}-${sidebarTab}-${isComposerTab ? sidebarPulseKey : 0}`}
                                sx={[
                                    tabContentBoxStyle,
                                    {
                                        position: 'relative',
                                        // Opacity-only entrance — intentionally NO translateY/scale.
                                        // A transform-based animation shifts the ReactQuill .ql-editor's
                                        // hit-area during the animation, overlapping with the invisible
                                        // DueDatetime input and making the composer body unclickable until
                                        // the animation completes. Pure fade avoids any layout/hit-test shift.
                                        '@keyframes composerEntrance': {
                                            '0%':   { opacity: 0 },
                                            '100%': { opacity: 1 },
                                        },
                                        animation: 'composerEntrance 240ms ease',
                                    },
                                ]}
                            >
                                {/* Scoped composer glow — an inset accent ring on the
                                    composer content only (never the nav rail). Pulses
                                    twice then fades. pointerEvents:none. */}
                                {isComposerTab && sidebarPulseKey > 0 && (
                                    <Box
                                        key={`composer-glow-${sidebarPulseKey}`}
                                        data-testid="composer-attention-glow"
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            zIndex: 4,
                                            pointerEvents: 'none',
                                            borderRadius: 'inherit',
                                            '@keyframes composerGlowPulse': {
                                                '0%':   { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.85), inset 0 0 16px 2px rgba(7,141,238,0.30)' },
                                                '50%':  { boxShadow: 'inset 0 0 0 1px rgba(7,141,238,0.20), inset 0 0 6px 1px rgba(7,141,238,0.10)' },
                                                '100%': { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.85), inset 0 0 16px 2px rgba(7,141,238,0.30)' },
                                            },
                                            '@keyframes composerGlowFade': {
                                                '0%':   { opacity: 1 },
                                                '80%':  { opacity: 1 },
                                                '100%': { opacity: 0 },
                                            },
                                            animation: 'composerGlowPulse 0.7s ease-in-out 2, composerGlowFade 1.5s ease-out forwards',
                                        }}
                                    />
                                )}
                                {/* Accent sweep — a single diagonal shimmer across the
                                    composer on open (composer tabs only). */}
                                {isComposerTab && sidebarPulseKey > 0 && (
                                    <Box
                                        key={`composer-sweep-${sidebarPulseKey}`}
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            zIndex: 3,
                                            pointerEvents: 'none',
                                            borderRadius: 'inherit',
                                            overflow: 'hidden',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '60%',
                                                height: '100%',
                                                background: 'linear-gradient(105deg, transparent 0%, rgba(7,141,238,0.14) 50%, transparent 100%)',
                                                transform: 'translateX(-120%)',
                                                '@keyframes composerSweep': {
                                                    '0%':   { transform: 'translateX(-120%)' },
                                                    '100%': { transform: 'translateX(220%)' },
                                                },
                                                animation: 'composerSweep 0.85s ease-out forwards',
                                            },
                                        }}
                                    />
                                )}
                                {tab.component}
                            </Box>
                        );
                    })}

                    <Box sx={tabsMainBoxStyle}>
                        <Tabs
                            orientation="vertical"
                            variant="fullWidth"
                            value={sidebarTab}
                            onChange={onTabChange}
                            sx={{ '.MuiTabs-flexContainer': { gap: 1 } }}
                        >
                            {sidebarTabs.map((tab, index) => (
                                <Tab
                                    icon={
                                        <Iconify
                                            icon={tab.icon}
                                            width={tab.width}
                                            height={tab.height}
                                            sx={{ mr: '0 !important', mb: '4px' }}
                                        />
                                    }
                                    key={index}
                                    label={labelCount(tab)}
                                    value={tab.value}
                                    sx={tabButtonStyle}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </Stack>
        </Dialog>
        </PopupFailureProvider>
    );
}

AttendedFullscreenWrapper.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    type: PropTypes.string,
    headerButtons: PropTypes.bool,
    headerTitle: PropTypes.node,
    followLead: PropTypes.object,
    appointmentFilter: PropTypes.func,
    sidebarTabs: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            component: PropTypes.node.isRequired,
            width: PropTypes.number,
            height: PropTypes.number,
        })
    ).isRequired,
    sidebarTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
    sidebarPulseKey: PropTypes.number,
    pulseCssName: PropTypes.string,
    /** Change 5: checklist items that are done. 0 = unknown → progress section hidden. */
    checklistDone: PropTypes.number,
    /** Change 5: total checklist items. 0 = unknown → progress section hidden. */
    checklistTotal: PropTypes.number,
    popupName: PropTypes.string,
    pinLeadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pinLeadPracticeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node.isRequired,
};

AttendedFullscreenWrapper.defaultProps = {
    type: 'lead',
    headerButtons: false,
    headerTitle: null,
    followLead: null,
    appointmentFilter: null,
    sidebarPulseKey: 0,
    pulseCssName: 'attendedWrapperPulse',
    checklistDone: 0,
    checklistTotal: 0,
    popupName: 'Action popup',
    pinLeadId: undefined,
    pinLeadPracticeId: undefined,
};
